import type { TreeNodeType } from '@/shared/lib/tree'

import React, { Fragment, useState } from 'react'
import { uniq, xor } from 'lodash/fp'
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react'

import { Checkbox } from '@/shared/components/ui/checkbox-native'
import { Label } from '@/shared/components/ui/label'
import {
	getChildrenIds,
	normalizeToEndNodes,
	toggleItem,
} from '@/shared/lib/tree'
import { cn } from '@/shared/lib/utils'

export default function TreeCheckbox({
	tree,
	disabled,
	value: selected = [],
	onChange,
}: {
	disabled?: boolean
	tree: TreeNodeType[]
	value: string[]
	onChange: (value: string[]) => void
}) {
	const [expanded, setExpanded] = useState<string[]>([])
	const setSelected = (ids: string[]) =>
		onChange(normalizeToEndNodes(tree, ids))
	const isExpanded = (id: string) => expanded.includes(id)
	const isSelected = (id: string) => selected.includes(id)
	const handleToggleExpand = (id: string) =>
		setExpanded(toggleItem(expanded, id))

	const renderItem = (
		level: number,
		node: TreeNodeType,
		index: number,
		nodesCount: number
	) => {
		const isFirst = index === 0
		const isLast = index === nodesCount - 1
		const canExpand = Boolean(node.children?.length)
		const childrenIds = getChildrenIds(node.children || [])
		const expanded = isExpanded(node.id)
		const checked =
			isSelected(node.id) ||
			(canExpand && childrenIds.every((id) => selected.includes(id)))
		const indeterminate = childrenIds.some((id) => selected.includes(id))

		return (
			<Fragment key={node.id}>
				<li
					className={cn('tree-node', {
						last: isLast,
						first: isFirst,
						expanded,
					})}
				>
					<div className='content'>
						<div className='inline-flex items-center'>
							{canExpand && (
								<div className='flex w-5 items-center'>
									<button
										type='button'
										className='flex cursor-pointer items-center justify-center border-none bg-transparent'
										data-nodeid={node.id}
										onClick={() => handleToggleExpand(node.id)}
									>
										{expanded ? (
											<ChevronDownIcon className='size-4' />
										) : (
											<ChevronRightIcon className='size-4' />
										)}
									</button>
								</div>
							)}
							<label className='flex items-center gap-2'>
								<Checkbox
									disabled={disabled}
									className='mr-1'
									data-nodeid={node.id}
									checked={checked}
									indeterminate={!checked && indeterminate}
									onChange={() => {
										// Has children
										if (canExpand) {
											if (checked) {
												setSelected(xor(selected, childrenIds))
											} else {
												setSelected(uniq([...selected, ...childrenIds]))
											}
										} else {
											if (checked) {
												setSelected(xor(selected, [node.id]))
											} else {
												setSelected(uniq([...selected, node.id]))
											}
										}
									}}
								/>
								<Label className='font-normal select-none'>{node.name}</Label>
							</label>
						</div>
					</div>
					{canExpand && expanded && (
						<ul>{renderTree(node.children || [], level + 1)}</ul>
					)}
				</li>
			</Fragment>
		)
	}
	const renderTree = (nodes: TreeNodeType[], level: number) => (
		<>
			{nodes.map((node, index) => renderItem(level, node, index, nodes.length))}
		</>
	)

	return <ul className='cmp-tree'>{!!tree?.length && renderTree(tree, 0)}</ul>
}
